import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { http } from "../../shared/api/http.js";
import type { FeedItemDto, FeedPage } from "@smartvow/shared";

export type { FeedItemDto };

const feedKey = (eventId: string) => ["feed", eventId] as const;

export function useFeed(eventId: string, token: string | undefined, email: string) {
  return useInfiniteQuery({
    queryKey: feedKey(eventId),
    enabled: !!token,
    initialPageParam: undefined as string | undefined,
    refetchInterval: 8000, // filet : marche même sans WebSocket
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ t: token! });
      if (email) q.set("email", email);
      if (pageParam) q.set("cursor", pageParam);
      return http.get<FeedPage>(`/events/${eventId}/feed?${q}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useLike(eventId: string, token: string | undefined, email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, like }: { id: string; like: boolean }) =>
      like
        ? http.post(`/media/${id}/like?t=${encodeURIComponent(token!)}`, { email })
        : http.del(`/media/${id}/like?t=${encodeURIComponent(token!)}`),
    onMutate: async ({ id, like }) => {
      await qc.cancelQueries({ queryKey: feedKey(eventId) });
      const prev = qc.getQueryData(feedKey(eventId));
      qc.setQueryData(feedKey(eventId), (old: { pages: FeedPage[] } | undefined) =>
        old
          ? {
              ...old,
              pages: old.pages.map((p) => ({
                ...p,
                items: p.items.map((it) =>
                  it.id === id
                    ? { ...it, likedByMe: like, likeCount: it.likeCount + (like ? 1 : -1) }
                    : it,
                ),
              })),
            }
          : old,
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(feedKey(eventId), ctx.prev),
  });
}

/** WebSocket temps réel ; rafraîchit le fil à chaque événement. Repli silencieux sur le polling. */
export function useEventSocket(eventId: string, token: string | undefined) {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const retry = useRef(0);

  useEffect(() => {
    if (!token) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let timer: number;

    const open = () => {
      const proto = location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(
        `${proto}://${location.host}/api/events/${eventId}/ws?t=${encodeURIComponent(token)}`,
      );
      ws.onopen = () => {
        retry.current = 0;
        setConnected(true);
      };
      ws.onmessage = () => qc.invalidateQueries({ queryKey: feedKey(eventId) });
      ws.onclose = () => {
        setConnected(false);
        if (closed) return;
        const delay = Math.min(1000 * 2 ** retry.current++, 15000);
        timer = window.setTimeout(open, delay);
      };
      ws.onerror = () => ws?.close();
    };
    open();

    return () => {
      closed = true;
      window.clearTimeout(timer);
      ws?.close();
    };
  }, [eventId, token, qc]);

  return connected;
}
